import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MousePointer2, ArrowDown, Package, ShoppingBag, MessageCircle, ShieldCheck } from "lucide-react";
import { EASE, scrollToId } from "./Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatChip = ({ icon: Icon, big, small, delay, y, testId }) => (
  <motion.div
    style={{ y }}
    className="pointer-events-none"
    initial={{ opacity: 0, scale: 0.85, y: 30 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 1, delay, ease: EASE }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-ecnavy/10 rounded-2xl px-5 py-4 shadow-[0_16px_40px_rgba(22,48,92,0.12)]">
      <span className="h-10 w-10 rounded-xl bg-ecamber/15 text-ecamber flex items-center justify-center">
        <Icon size={20} strokeWidth={2.2} />
      </span>
      <div>
        <p className="font-display text-lg font-semibold text-ecdeep leading-none">{big}</p>
        <p className="text-xs text-slate-500 mt-1">{small}</p>
      </div>
    </div>
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yHead = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yChip1 = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yChip2 = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const yChip3 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yChip4 = useTransform(scrollYProgress, [0, 1], [0, -170]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} id="portada" className="relative min-h-screen flex flex-col overflow-hidden bg-white" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid-bg" />
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-ecamber/[0.14] blur-[120px]"
      />
      <div className="absolute -bottom-52 -left-40 h-[480px] w-[480px] rounded-full bg-ecnavy/[0.07] blur-[120px]" />

      <div className="relative flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 pt-40 pb-24 grid lg:grid-cols-[1.5fr_1fr] gap-16 items-center">
        <motion.div style={{ y: yHead }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-ecnavy/15 bg-white px-5 py-2 mb-10 shadow-sm"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-ecamber animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ecnavy">Portafolio Corporativo 2026</span>
          </motion.div>

          <motion.img
            src="/assets/eclickgo-logo.webp"
            alt="eClickGo"
            className="h-14 md:h-20 w-auto mb-10"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease: EASE }}
            data-testid="hero-logo"
          />

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.4rem] font-semibold tracking-tight text-ecdeep leading-[1.05]" data-testid="hero-title">
            <MaskedLine delay={0.5}>Soluciones</MaskedLine>
            <MaskedLine delay={0.62}>
              Tecnológicas <span className="text-ecamber">para</span>
            </MaskedLine>
            <MaskedLine delay={0.74}>
              <span className="relative inline-flex items-center">
                Retail y Empresas
                <motion.span
                  initial={{ opacity: 0, x: 40, y: -40, rotate: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 120, damping: 12, delay: 1.5 }}
                  className="ml-4 text-ecamber"
                >
                  <MousePointer2 size={44} strokeWidth={2.4} fill="#F5A81C" />
                </motion.span>
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-slate-600 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Proveedor nacional de tecnología para cadenas de retail, empresas y gobierno.
            Productos originales, facturación CFDI y cobertura en toda la República Mexicana.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("contacto")}
              className="group rounded-full bg-ecnavy text-white font-semibold px-8 py-4 text-sm hover:bg-ecdeep transition-colors duration-300 shadow-[0_16px_40px_rgba(22,48,92,0.3)] flex items-center gap-2"
              data-testid="hero-cta-primary"
            >
              Iniciar conversación
              <MousePointer2 size={16} className="text-ecamber transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToId("nosotros")}
              className="rounded-full border border-ecnavy/20 text-ecnavy font-semibold px-8 py-4 text-sm hover:bg-ecnavy/5 transition-colors duration-300"
              data-testid="hero-cta-secondary"
            >
              Conocer eClick
            </button>
          </motion.div>
        </motion.div>

        <div className="hidden lg:flex flex-col gap-5 items-start pl-8">
          <FloatChip icon={Package} big="800+ pedidos" small="Amazon · último año" delay={1.3} y={yChip1} testId="hero-chip-amazon" />
          <div className="ml-16">
            <FloatChip icon={ShoppingBag} big="77+ pedidos" small="Mercado Libre · último año" delay={1.45} y={yChip2} testId="hero-chip-mercadolibre" />
          </div>
          <FloatChip icon={MessageCircle} big="73+ ventas directas" small="Canal B2B WhatsApp" delay={1.6} y={yChip3} testId="hero-chip-whatsapp" />
          <div className="ml-16">
            <FloatChip icon={ShieldCheck} big="Distribuidor autorizado" small="Samsung · Syscom · Ingram Micro" delay={1.75} y={yChip4} testId="hero-chip-distribuidor" />
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative border-t border-ecnavy/10 bg-white/70 backdrop-blur-sm overflow-hidden"
        data-testid="hero-marquee"
      >
        <div className="marquee-track-slow py-4">
          {[0, 1].map((n) => (
            <div key={n} className="flex items-center shrink-0">
              {["Proveedor Nacional de Tecnología", "Atención B2B", "Facturación CFDI", "Compras por Volumen", "Garantía Oficial", "Cobertura Nacional", "Productos Originales", "Entregas Programadas"].map((t) => (
                <span key={t} className="flex items-center gap-6 px-6 text-xs font-semibold uppercase tracking-[0.28em] text-ecnavy/45 whitespace-nowrap">
                  {t}
                  <span className="h-1.5 w-1.5 rounded-full bg-ecamber" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("nosotros")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 2 }, y: { repeat: Infinity, duration: 2, ease: "easeInOut" } }}
        className="absolute bottom-24 right-10 hidden md:flex h-12 w-12 rounded-full border border-ecnavy/15 bg-white items-center justify-center text-ecnavy hover:border-ecamber transition-colors"
        data-testid="hero-scroll-indicator"
        aria-label="Bajar"
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
}
